import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { decode as base64_decode } from 'base-64';
import { useRealtime } from './RealtimeProvider';

const MAX_ITEMS = 120;
const TTL_MS = 24 * 60 * 60 * 1000;
const STORAGE_BASE_KEY = 'rt_inbox_v2';

const NotificationsContext = createContext(null);

const getEmpresaId = () => {
  try {
    const usersRt = sessionStorage.getItem('ddesp');
    if (!usersRt) return null;
    const users = JSON.parse(base64_decode(usersRt));
    return users?.[0] || null;
  } catch {
    return null;
  }
};

const buildStorageKey = (empresaId) => 
  empresaId ? `${STORAGE_BASE_KEY}_emp_${empresaId}` : STORAGE_BASE_KEY;

const getStorageKey = () => {
  const empresaId = getEmpresaId();
  return buildStorageKey(empresaId);
};

const loadItemsFromStorage = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    const arr = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    return arr.filter((n) => now - (n.ts || now) < TTL_MS).slice(-MAX_ITEMS);
  } catch {
    return [];
  }
};

const normalizeNotification = (event) => {
  const raw = event && typeof event === 'object' ? event : {};
  const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
  const meta = raw.meta && typeof raw.meta === 'object' ? raw.meta : {};

  const title = payload.title || raw.title || meta.title || 'Notificação';
  const message = payload.message || payload.text || raw.message || raw.text || '';
  const severity = (payload.severity || payload.level || raw.severity || raw.level || 'info').toLowerCase();
  const persist = raw.persist !== undefined ? raw.persist : (payload.persist !== undefined ? payload.persist : (meta.persist !== undefined ? meta.persist : true));
  
  const tsCandidate = payload.timestamp ?? payload.ts ?? raw.timestamp ?? raw.ts ?? Date.now();
  const ts = Number.isFinite(Number(tsCandidate)) ? Number(tsCandidate) : Date.now();

  const rawId = payload.id || raw.id || meta.id;
  const id = rawId || `${ts}-${Math.random().toString(36).slice(2, 8)}`;

  const link = payload.link || meta.link || null;

  return {
    id,
    ts,
    severity,
    title,
    message,
    persist,
    link,
    raw,
    payload,
    meta,
  };
};

export function NotificationsProvider({children}) {
  const {on} = useRealtime();
  const storageKeyRef = useRef(getStorageKey());
  const [items, setItems] = useState(() => loadItemsFromStorage(storageKeyRef.current));
  const unreadRef = useRef(new Set(items.filter((i) => !i.read).map((i) => i.id)));

  const persist = (arr) => {
    const storageKey = storageKeyRef.current;
    if (!storageKey) return;
    if (!arr || arr.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(arr));
    }
  };

  const syncUnread = (list) => {
    const nextSet = new Set(list.filter((i) => !i.read).map((i) => i.id));
    unreadRef.current = nextSet;
    return nextSet;
  };

  const addItem = (normalized) => {
    const item = {
      id: normalized.id,
      ts: normalized.ts,
      severity: normalized.severity,
      title: normalized.title,
      text: normalized.message,
      link: normalized.link,
      read: false,
      data: {
        payload: normalized.payload,
        meta: normalized.meta,
        raw: normalized.raw,
      },
    };
    
    setItems((prev) => {
      const next = [...prev.filter((x) => x.id !== item.id), item].slice(-MAX_ITEMS);
      syncUnread(next);
      persist(next);
      return next;
    });
  };

  const showToast = (normalized) => {
    const severityMap = {
      low: { icon: 'ℹ️', color: '#3085d6', bg: '#d1ecf1', timer: 15000 },
      medium: { icon: '⚠️', color: '#f8bb86', bg: '#fff3cd', timer: 20000 },
      high: { icon: '❌', color: '#f27474', bg: '#f8d7da', timer: null },
      info: { icon: 'ℹ️', color: '#3085d6', bg: '#d1ecf1', timer: 12000 },
      success: { icon: '✓', color: '#a5dc86', bg: '#d4edda', timer: 12000 },
      warning: { icon: '⚠️', color: '#f8bb86', bg: '#fff3cd', timer: 15000 },
      error: { icon: '❌', color: '#f27474', bg: '#f8d7da', timer: null },
    };

    const cfg = severityMap[normalized.severity] || severityMap.info;
    const timer = cfg.timer;

    toast.custom(
      (t) => (
        <div
          onClick={() => toast.dismiss(t.id)}
          style={{
            display: t.visible ? 'flex' : 'none',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            background: '#fff',
            color: '#545454',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            border: `1px solid ${cfg.color}`,
            minWidth: '300px',
            maxWidth: '400px',
            fontSize: '14px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              background: cfg.bg,
              color: cfg.color,
              flexShrink: 0,
            }}
          >
            {cfg.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '600', marginBottom: normalized.message ? '4px' : 0, fontSize: '16px' }}>
              {normalized.title}
            </div>
            {normalized.message && (
              <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.4' }}>
                {normalized.message}
              </div>
            )}
          </div>
        </div>
      ),
      {
        duration: timer,
        position: 'top-right',
      }
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const refreshStorageKey = () => {
      const nextKey = getStorageKey();
      const currentKey = storageKeyRef.current;
      if (!nextKey || nextKey === currentKey) return;

      const nextItems = loadItemsFromStorage(nextKey);
      if (nextItems.length > 0) {
        storageKeyRef.current = nextKey;
        syncUnread(nextItems);
        setItems(nextItems);
        return;
      }

      const previousItems = currentKey ? loadItemsFromStorage(currentKey) : [];
      storageKeyRef.current = nextKey;
      if (previousItems.length > 0) {
        syncUnread(previousItems);
        setItems(previousItems);
        persist(previousItems);
        if (currentKey) localStorage.removeItem(currentKey);
      } else {
        syncUnread([]);
        setItems([]);
        persist([]);
        if (currentKey) localStorage.removeItem(currentKey);
      }
    };

    refreshStorageKey();

    const intervalId = setInterval(refreshStorageKey, 2000);
    window.addEventListener('focus', refreshStorageKey);
    window.addEventListener('storage', refreshStorageKey);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refreshStorageKey);
      window.removeEventListener('storage', refreshStorageKey);
    };
  }, []);

  useEffect(() => {
    if (!on) return;

    const handleNotify = (event) => {
      if (!event || typeof event !== 'object') return;

      const action = (event.action || '').toLowerCase();
      if (action !== 'notify') return;

      const normalized = normalizeNotification(event);
      if (!normalized) return;


      showToast(normalized);
    
    };

    const unsubControl = on('control', handleNotify);
    
    return () => unsubControl?.();
  }, [on]);

  const remove = (id) => {
    setItems((prev) => {
      const next = prev.filter((x) => x.id !== id);
      syncUnread(next);
      persist(next);
      return next;
    });
  };

  const clear = () => {
    setItems([]);
    syncUnread([]);
    persist([]);
  };

  const markRead = (id) => {
    setItems((prev) => {
      const next = prev.map((x) => (x.id === id ? {...x, read: true} : x));
      syncUnread(next);
      persist(next);
      return next;
    });
  };

  const markAllRead = () => {
    setItems((prev) => {
      const next = prev.map((x) => ({...x, read: true}));
      syncUnread(next);
      persist(next);
      return next;
    });
  };

  const unreadCount = () => unreadRef.current.size;

  const value = useMemo(
    () => ({
      items,
      add: (n) => {
        const normalized = normalizeNotification(n);
        showToast(normalized);
        if (normalized.persist) addItem(normalized);
      },
      remove,
      clear,
      markRead,
      markAllRead,
      unreadCount,
    }),
    [items]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
